import AuthDataService from "@/services/AuthDataService"
import AccountDataService from "@/services/AccountDataService"
import { AlertType } from "@/static/Alert"
import { UserRole } from "@/static/User"
import type { IUserAccount } from "@/types/Account.types"
import { defineStore } from "pinia"
import { useAlertStore } from "@/stores/alert"
import { useAccountStore } from "@/stores/account"
import { useLocalDiffuserStore } from "@/stores/localDiffuser"
import { useNotificationCenterStore } from "@/stores/notificationCenter"
import { useSpaceStore } from "@/stores/space"
import { i18n } from "@/i18n"
import { ConfigurationState, configurationStateRouteName } from "@/static/Configuration"
import {useEstablishmentStore} from "@/stores/establishment";
import { useTrackingStore } from "@/stores/tracking"
import { nextTick } from "vue"
import {
  appendDialogueSourceToRedirect,
  isDialogueRedirectPath,
  resolvePostLoginNavigation,
} from "@/utils/dialogueRedirect"
import { getAccessPlanCode } from "@/utils/contract"
import {
  buildPostLoginNavigation,
  type PostLoginRedirectContext,
} from "@/utils/postLoginRedirect"

export interface MagicLinkUrlParams {
  redirect?: string | null
  establishmentId?: string | null
  source?: string | null
}

export const useAuthenticationStore = defineStore("authentication", {
  state: () => ({
    /** @type {string} current authenticated user email */
    email: "",

    /** @type {IUserAccount | null} current authenticated user from local storage */
    user: JSON.parse(localStorage.getItem("user") || "{}") as IUserAccount | null,

    /** @type {string} current authenticated user token */
    token: localStorage.getItem("token"),

    /** @type {string} current authenticated user token type */
    tokenType: localStorage.getItem("tokenType"),
  }),

  getters: {
    /**
     * Get the current authenticated authorization as query header
     * @param state authenticationStore state
     * @returns {{Authorization: string}} The complete header
     */
    currentHeaders: (state): { Authorization: string } => {
      return { Authorization: state.tokenType + " " + state.token }
    },

    /**
     * Check if current authenticated user is an administrator
     * @param state authenticationStore state
     * @returns {Boolean}
     */
    isAdmin: (state): boolean => {
      return state.user?.role.code === UserRole.admin
    },

    isEditor: (state): boolean => {
      return state.user?.role.code === UserRole.editor
    },

    /**
     * Privilèges commerciaux : rôles SALE et CSM (mêmes droits, CSM a en plus l'inbox support).
     */
    isSale: (state): boolean => {
      const code = state.user?.role?.code
      return code === UserRole.sale || code === UserRole.csm
    },

    /** Rôle SALE strict (hors CSM). */
    isSaleOnly: (state): boolean => {
      return state.user?.role?.code === UserRole.sale
    },

    isCsm: (state): boolean => {
      return state.user?.role?.code === UserRole.csm
    },

    isGod: (state): boolean => {
      return state.user?.role.code === UserRole.god
    },

    isStaffSupport: (state): boolean => {
      const code = state.user?.role?.code
      return code === UserRole.god || code === UserRole.csm
    },

    /**
     * Check if current user's account is a partner account
     * Partner accounts are: 'Petit Futé', 'OT Manosque', 'Edgar'
     * Non-partner (internal) account is: 'Uska'
     * @param state authenticationStore state
     * @returns {Boolean}
     */
    isPartnerAccount: (state): boolean => {
      return state.user?.account?.partner === true || state.user?.account?.is_partner === true
    },

    /**
     * Check if current user's account is a sales account
     * @param state authenticationStore state
     * @returns {Boolean}
     */
    isSalesAccount: (state): boolean => {
      return state.user?.account?.sale_account === true || state.user?.account?.is_sales_account === true
    },

    /**
     * Check if the sale user's account (commercial's account) is a partner account
     * @param state authenticationStore state
     * @returns {Boolean}
     */
    isSaleUserAccountPartner: (state): boolean => {
      const saleUserAccount = state.user?.account?.sale_user?.account
      return saleUserAccount?.partner === true || saleUserAccount?.is_partner === true
    },

    /**
     * Campagnes emailing : réservées aux commerciaux des comptes sales non partenaires (ex. Uska).
     */
    canAccessSalesCampaigns: (state): boolean => {
      const code = state.user?.role?.code
      const hasSalePrivileges = code === UserRole.sale || code === UserRole.csm
      return (
        hasSalePrivileges
        && state.user?.account?.partner !== true
        && state.user?.account?.is_partner !== true
      )
    },
  },

  actions: {
    /**
     * Clear all store states
     */
    clear() {
      this.$patch({
        email: "",
        user: null,
        token: "",
        tokenType: "",
      })
      localStorage.clear()
    },

    /**
     * Check reason of 401 request error and logout
     * @param code
     * @param callback
     */
    authenticationError(code: string, callback: () => void) {
      const alerts = useAlertStore()
      switch (code) {
        case "NOT_FOUND_TOKEN":
          alerts.push(AlertType.error, i18n.global.t("common.errors.no_connected_user"))
          this._authenticationErrorLogout()
          break
        case "EXPIRED_TOKEN":
          alerts.push(AlertType.error, i18n.global.t("common.errors.user_session_expired"))
          this._authenticationErrorLogout()
          break
        case "INVALID_TOKEN":
          alerts.push(AlertType.error, i18n.global.t("common.errors.user_session_invalid"))
          this._authenticationErrorLogout()
          break
        default:
          callback()
          break
      }
    },

    /**
     * Logout after 401 request error
     * @private
     */
    _authenticationErrorLogout() {
      this.logout()
      this.clear()
      this.router.push({ name: "auth" })
    },

    // CONNEXION
    /**
     * Initialize authenticated session variables
     * @param password
     * @param firstAttempt is the current user first connexion on current device
     */
    login(password: string, firstAttempt: boolean, errorCallback?: () => void) {
      AuthDataService.login(this.email, password).then(
        (res) => {
          const token = res.data.access_token
          const tokenType = res.data.token_type

          useTrackingStore().markAppSessionTracked()
          this.storeLoginUser(token, tokenType, firstAttempt)
        },
        (e) => {
          const alerts = useAlertStore()
          switch (e.response.status) {
            case 401:
              this.authenticationError(e.response.code, () => {
                switch (e.response.data.message) {
                  case "Email not verified":
                    alerts.push(
                      AlertType.error,
                      i18n.global.t("authentication.connexion_form.error_email_not_verified", e.message)
                    )
                    break
                  case "Bad credentials":
                    alerts.push(
                      AlertType.error,
                      i18n.global.t("authentication.connexion_form.error_wrong_email_or_password", e.message)
                    )
                    break
                  default:
                    alerts.push(AlertType.error, i18n.global.t("common.errors.user_access"))
                    break
                }
              })
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
          if (errorCallback) {
            errorCallback()
          }
        }
      )
    },

    /**
     * Charge les établissements, sélectionne celui demandé (magic link), puis redirige.
     * Priorité à redirectAfterLogin (ex. lien de souscription → /checkout).
     */
    navigateAfterLoginWithEstablishment(
      redirectPath: string | null,
      requestedEstablishmentId: string | null,
      fallback: () => void
    ) {
      const accountStore = useAccountStore()
      const establishmentStore = useEstablishmentStore()

      accountStore.getEstablishments(() => {
        establishmentStore.initEstablishment(requestedEstablishmentId).then(() => {
          nextTick(() => {
            sessionStorage.removeItem("requestedEstablishmentId")
            sessionStorage.removeItem("redirectAfterLogin")

            if (redirectPath && redirectPath !== "/authentication") {
              const destination = resolvePostLoginNavigation(redirectPath)
              if (destination) {
                this.router.push(destination)
              } else {
                fallback()
              }
            } else {
              fallback()
            }
          })
        }).catch(() => {
          sessionStorage.removeItem("requestedEstablishmentId")
          sessionStorage.removeItem("redirectAfterLogin")
          if (redirectPath && redirectPath !== "/authentication") {
            const destination = resolvePostLoginNavigation(redirectPath)
            if (destination) {
              this.router.push(destination)
            } else {
              this.router.push({ name: "home" })
            }
          } else {
            this.router.push({ name: "home" })
          }
        })
      }, false)
    },

    storeLoginUser(
      token: string,
      tokenType: string,
      firstAttempt: Boolean,
      magicLinkUrlParams?: MagicLinkUrlParams
    ) {
      if (magicLinkUrlParams) {
        this.completeMagicLinkLogin(token, tokenType, magicLinkUrlParams)
        return
      }

      const alerts = useAlertStore()
      const establishmentStore = useEstablishmentStore()

      this.token = token
      this.tokenType = tokenType

      localStorage.setItem("token", token)
      localStorage.setItem("tokenType", tokenType)

      this.me(() => {
        // Si l'utilisateur n'a pas de compte, considérer qu'il est configuré et le rediriger vers home
        const isConfigured = !this.user!.account || this.user!.account.configured
        const rawRedirectPath = sessionStorage.getItem("redirectAfterLogin")
        const redirectPath =
          rawRedirectPath && isDialogueRedirectPath(rawRedirectPath)
            ? appendDialogueSourceToRedirect(
                rawRedirectPath,
                sessionStorage.getItem("requestedDialogueSource")
              )
            : rawRedirectPath
        const requestedEstablishmentId = sessionStorage.getItem("requestedEstablishmentId") ?? null
        const hasMagicLinkRedirect =
          !!redirectPath &&
          redirectPath !== "/authentication" &&
          redirectPath.startsWith("/") &&
          !redirectPath.startsWith("//")

        // Si l'utilisateur a configuré son compte
        if (isConfigured) {
          this.navigateAfterLoginWithEstablishment(redirectPath, requestedEstablishmentId, () => {
            const isDemoEstablishment =
              getAccessPlanCode(establishmentStore.currentEstablishment) === "DEMO"

            if (isDemoEstablishment) {
              this.router.push({ name: "editorial-calendar" })
            } else if (this.isSale) {
              this.router.push({ name: "sales-contracts-management" })
            } else {
              this.router.push({ name: "home" })
            }
          })
        } else if (hasMagicLinkRedirect) {
          // Liens magiques (checkout, notifications, etc.) : respecter la cible même si le compte n'est pas configuré (ex. DEMO)
          this.navigateAfterLoginWithEstablishment(redirectPath, requestedEstablishmentId, () => {
            const destination = resolvePostLoginNavigation(redirectPath!)
            if (destination) {
              this.router.push(destination)
            }
          })
        } else {
          const state = this.user!.account.configuration_state

          // Si c'est la première connexion de l'utilisateur sur cet appareil
          if (firstAttempt) {
            this.router.push({ name: "app-configuration" })
          } else {
            // Si l'état courant de la configuration est défini
            if (state) {
              // Si c'est un compte DEMO, rediriger vers le calendrier éditorial
              if (state === ConfigurationState.DEMO) {
                this.router.push({ name: "editorial-calendar" })
              }
              // Si on est en onboarding (START_CONNEXION ou START_LISTING), rediriger vers /configuration
              else if (state === ConfigurationState.START_CONNEXION || state === ConfigurationState.START_LISTING) {
                this.router.push({ path: "/configuration" })
              } else {
                const route = configurationStateRouteName[state]
                if (route && route.startsWith("/")) {
                  this.router.push({ path: route })
                } else {
                  this.router.push({ name: route })
                }
              }
            } else {
              // Si l'état courant de la configuration n'est pas défini, vérifier si c'est un compte DEMO
              AccountDataService.establishments(this.currentHeaders).then(
                (res) => {
                  const establishments = res.data.data.establishments
                  // Vérifier si au moins un établissement a un contrat DEMO
                  const hasDemoContract = establishments.some(
                    (establishment: any) => getAccessPlanCode(establishment) === 'DEMO'
                  )
                  
                  if (hasDemoContract) {
                    // Mettre à jour le configuration_state à DEMO pour éviter les redirections par le middleware
                    AccountDataService.updateConfigurationState(
                      this.user!.account.id,
                      ConfigurationState.DEMO
                    ).then(() => {
                      // Mettre à jour l'utilisateur local
                      if (this.user?.account) {
                        this.user.account.configuration_state = ConfigurationState.DEMO
                        localStorage.setItem("user", JSON.stringify(this.user))
                      }
                      this.router.push({ name: "editorial-calendar" })
                    }).catch(() => {
                      // Même en cas d'erreur de mise à jour, rediriger vers le calendrier
                      this.router.push({ name: "editorial-calendar" })
                    })
                  } else {
                    this.router.push({ path: "/configuration" })
                  }
                },
                () => {
                  // En cas d'erreur, rediriger vers configuration par défaut
                  this.router.push({ path: "/configuration" })
                }
              )
            }
          }
        }
      })
    },

    /**
     * Enregistre le JWT et charge /auth/me sans redirection (création démo invité depuis la landing).
     */
    async applyJwtSession(accessToken: string, tokenType: string): Promise<void> {
      this.token = accessToken
      this.tokenType = tokenType
      localStorage.setItem("token", accessToken)
      localStorage.setItem("tokenType", tokenType)
      const res = await AuthDataService.me()
      const user = res.data.data.user
      this.user = user
      localStorage.setItem("user", JSON.stringify(user))
    },

    /**
     * Logout
     */
    logout() {
      if (this.currentHeaders.Authorization !== null && this.currentHeaders.Authorization !== " ") {
        AuthDataService.logout(this.currentHeaders).then(
          () => {
            localStorage.clear()
            sessionStorage.clear()
          },
          (e) => {
            const alerts = useAlertStore()
            switch (e.response.status) {
              case 401:
                break
              default:
                alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
          }
        )
      }
    },

    /**
     * Retrieve the current authenticated user
     * @param callback
     */
    me(callback: () => void) {
      const hop = () => {
        return AuthDataService.me().then(
          (res) => {
            const user = res.data.data.user
            this.user = user

            localStorage.setItem("user", JSON.stringify(user))
            callback()
          },
          (e) => {
            const alerts = useAlertStore()
            if (e.response) {
              switch (e.response.status) {
                case 426:
                  this.router.push({ name: "error-version" })
                  alerts.push(AlertType.info, "Merci de patienter encore quelques instants...")
                  break
                case 401:
                  break
                default:
                  alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                  break
              }
            } else {
              this.router.push({ name: "error" })
            }
          }
        )
      }

      if (
        (this.token === null && this.tokenType === null && localStorage.getItem("token") !== undefined) ||
        (this.token !== null && this.tokenType !== null)
      ) {
        if (localStorage.getItem("token") !== undefined) {
          return hop()
        }
      }
      // Toujours retourner une Promise, même si les conditions ne sont pas remplies
      return hop()
    },

    /**
     * Validate the new user email
     * @param token
     * @param callback
     * @param callbackError
     */
    validate(token: string, callback: () => void, callbackError: (message: string) => void) {
      AuthDataService.validate(token).then(
        (res) => {
          if (!res.data.success) {
            callbackError(res.data.message)
          } else {
            callback(res.data.data)
          }
        },
        (e) => {
          const alerts = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            case 400:
              alerts.push(AlertType.error, "Le lien de validation n'existe plus")
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    sendValidationEmail(callback: () => void) {
      if (this.user) {
        const alerts = useAlertStore()
        AuthDataService.resendValidationEmail(this.user?.email).then(
          (res) => {
            alerts.push(
              AlertType.success,
              i18n.global.t("common.messages.validation_mail_sent", { email: this.user?.email })
            )
            callback()
          },
          (e) => {
            switch (e.response.status) {
              case 401:
                break
              default:
                alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                break
            }
            callback()
          }
        )
      }
    },

    // FORGET PASSWORD
    /**
     * Send email with link to reset forgotten password
     * @param callback
     */
    forgetPasswordSendEmail(callback: () => void, errorCallback?: () => void) {
      AuthDataService.forgetPasswordSendEmail(this.email).then(
        (res) => {
          callback()
        },
        (e) => {
          const alerts = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            case 400:
              alerts.push(AlertType.error, i18n.global.t("common.errors.email_unknown"))
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
          if (errorCallback) {
            errorCallback()
          }
        }
      )
    },

    /**
     * Send magic link login email (always shows success on front for privacy)
     * @param callback
     * @param errorCallback
     */
    requestMagicLink(callback: () => void, errorCallback?: (status?: number) => void) {
      AuthDataService.requestMagicLink(this.email).then(
        () => {
          callback()
        },
        (e) => {
          const alerts = useAlertStore()
          const status = e.response?.status
          switch (status) {
            case 401:
              break
            case 422:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"))
              break
            case 429:
              alerts.push(
                AlertType.info,
                i18n.global.t("authentication.connexion_form.magic_link_rate_limit")
              )
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
          if (errorCallback) {
            errorCallback(status)
          }
        }
      )
    },

    /**
     * Check if given forgotten password token is valid
     * @param token
     * @param callback
     * @param callbackError
     */
    checkForgetToken(token: string, callback: (res: any) => void, callbackError: () => void) {
      const alerts = useAlertStore()
      AuthDataService.forgetPasswordCheckToken(token, this.currentHeaders).then(
        (res) => {
          if (res.data.data.exist) {
            callback(res)
          } else {
            callbackError()
            alerts.push(AlertType.error, i18n.global.t("authentication/resetpassword/validation.error.token_expired"))
          }
        },
        (e) => {
          callbackError()
          switch (e.response.status) {
            case 401:
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    /**
     * Reset the forgotten password
     * @param token
     * @param password
     * @param callback
     */
    resetPassword(token: string, password: string, callback: () => void) {
      AuthDataService.resetPassword(token, password, this.currentHeaders).then(
        () => {
          callback()
        },
        (e) => {
          const alerts = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            case 400:
              alerts.push(AlertType.error, i18n.global.t("authentication/resetpassword/validation.error.token_expired"))
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    // SETUP PASSWORD
    /**
     * Check if given new password token is valid
     * @param token
     * @param callback
     * @param callbackError
     */
    checkSetupToken(token: string, callback: (email: string) => void, callbackError: () => void, passwordAlreadySetCallback?: () => void) {
      const alerts = useAlertStore()
      AuthDataService.setupPasswordCheckToken(token, this.currentHeaders).then(
        (res) => {
          if (res.data.data.exist) {
            // Si le mot de passe est déjà créé, rediriger vers le dashboard
            if (res.data.data.password_already_set && passwordAlreadySetCallback) {
              passwordAlreadySetCallback()
              return
            }
            callback(res.data.data.email)
          } else {
            // Si le token n'existe plus (probablement déjà utilisé), rediriger vers le dashboard
            if (res.data.data.token_not_found && passwordAlreadySetCallback) {
              passwordAlreadySetCallback()
              return
            }
            callbackError()
            alerts.push(AlertType.error, i18n.global.t("authentication/resetpassword/validation.error.token_expired"))
          }
        },
        (e) => {
          callbackError()
          switch (e.response.status) {
            case 401:
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    /**
     * Set the new user password
     * @param token
     * @param password
     * @param callback
     */
    setupPassword(token: string, password: string, callback: () => void) {
      AuthDataService.setupPassword(token, password).then(
        (res) => {
          callback()
        },
        (e) => {
          const alerts = useAlertStore()
          switch (e.response.status) {
            case 401:
              break
            case 400:
              alerts.push(AlertType.error, i18n.global.t("authentication/resetpassword/validation.error.token_expired"))
              break
            default:
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },

    completeMagicLinkLogin(token: string, tokenType: string, urlParams: MagicLinkUrlParams) {
      this.token = token
      this.tokenType = tokenType

      localStorage.setItem("token", token)
      localStorage.setItem("tokenType", tokenType)

      useTrackingStore().markAppSessionTracked()

      this.me(() => {
        const accountStore = useAccountStore()
        const establishmentStore = useEstablishmentStore()

        const navigateWithContext = () => {
          nextTick(() => {
            const context: PostLoginRedirectContext = {
              user: this.user,
              hasDestinationProviders: accountStore.hasDestinationProviders,
              establishmentsCount: accountStore.establishments.length,
              accessPlanCode: getAccessPlanCode(establishmentStore.currentEstablishment),
              dialogueSource: urlParams.source,
            }

            const destination = buildPostLoginNavigation(urlParams.redirect, context, {
              establishmentId: urlParams.establishmentId,
              source: urlParams.source,
            })

            this.router.push(destination)
          })
        }

        accountStore.getEstablishments(() => {
          establishmentStore
            .initEstablishment(urlParams.establishmentId ?? null)
            .then(navigateWithContext)
            .catch(navigateWithContext)
        }, false)
      })
    },

    /**
     * Authenticate user via magic link
     * @param token
     * @param callback
     * @param errorCallback
     * @param urlParams Paramètres de redirection lus depuis l'URL
     */
    loginWithMagicLink(
      token: string,
      callback: () => void,
      errorCallback: (message: string) => void,
      urlParams?: MagicLinkUrlParams
    ) {
      const accountStore = useAccountStore()
      const establishmentStore = useEstablishmentStore()
      const localDiffuserStore = useLocalDiffuserStore()
      const notificationCenterStore = useNotificationCenterStore()
      const spaceStore = useSpaceStore()

      this.clear()
      accountStore.clear()
      establishmentStore.clear()
      localDiffuserStore.clear()
      notificationCenterStore.clear()
      spaceStore.clear()

      AuthDataService.loginWithMagicLink(token).then(
        (res) => {
          if (res.data.success) {
            this.storeLoginUser(res.data.access_token, res.data.token_type, false, urlParams)
            callback()
          } else {
            errorCallback(res.data.message || "Token invalide")
          }
        },
        (e) => {
          const alerts = useAlertStore()
          switch (e.response?.status) {
            case 401:
              errorCallback(e.response.data.message || "Token invalide ou expiré")
              alerts.push(AlertType.error, "Le lien de connexion n'est plus valide")
              break
            case 400:
              errorCallback(e.response.data.message || "Requête invalide")
              alerts.push(AlertType.error, "Le lien de connexion est invalide")
              break
            default:
              errorCallback(i18n.global.t("common.errors.default"))
              alerts.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
              break
          }
        }
      )
    },
  },

  persist: {
    enabled: true,
  },
})
